import React from 'react';
import { Tooltip, Space, Tag } from 'antd';
import { CheckCircleOutlined, SafetyCertificateOutlined, SafetyOutlined } from '@ant-design/icons';

/**
 * UserBadges Component
 * 
 * A reusable component to display user verification badges:
 * - Verified Badge (blue checkmark)
 * - Seller Trust Badge (green certificate)
 * - Buyer Protection Plus (orange safety icon)
 * 
 * @param {Object} user - User object with badge properties (is_verified, seller_trust_badge, buyer_protection_plus)
 * @param {Boolean} showLabels - Whether to show badge labels (default: false, shows only icons)
 * @param {String} size - Size variant (default: 'default')
 * @param {Number} iconSize - Size of icons in pixels (default: 16)
 * 
 * @example
 * // Icon-only badges (compact)
 * <UserBadges user={user} iconSize={14} />
 * 
 * @example
 * // Badges with labels
 * <UserBadges user={user} showLabels={true} iconSize={18} />
 * 
 * Usage in different components:
 * - Profile page: Shows badges with labels
 * - ContactAgentsOwner: Shows icon-only badges next to names
 * - Admin Users: Shows icon-only badges in table
 * - SideMenu: Shows icon-only badges in user info
 */
const UserBadges = ({ 
    user, 
    showLabels = false, 
    size = 'default',
    iconSize = 16 
}) => {
    if (!user) return null;

    const badges = [];

    // Verified Badge
    if (user.is_verified) {
        badges.push(
            <Tooltip key="verified" title="Verified Account">
                {showLabels ? (
                    <Tag color="blue" icon={<CheckCircleOutlined />}>
                        Verified
                    </Tag>
                ) : (
                    <CheckCircleOutlined 
                        style={{ 
                            color: '#1890ff', 
                            fontSize: iconSize
                        }} 
                    />
                )}
            </Tooltip>
        );
    }

    // Seller Trust Badge
    if (user.seller_trust_badge) {
        badges.push(
            <Tooltip key="seller-trust" title="Seller Trust Badge - Trusted seller with verified credentials">
                {showLabels ? (
                    <Tag color="green" icon={<SafetyCertificateOutlined />}>
                        Trusted Seller
                    </Tag>
                ) : (
                    <SafetyCertificateOutlined 
                        style={{ 
                            color: '#52c41a', 
                            fontSize: iconSize
                        }} 
                    />
                )}
            </Tooltip>
        );
    }

    // Buyer Protection Plus
    if (user.buyer_protection_plus) {
        badges.push(
            <Tooltip key="buyer-protection" title="Buyer Protection Plus - Enhanced buyer protection">
                {showLabels ? (
                    <Tag color="orange" icon={<SafetyOutlined />}>
                        Buyer Protection Plus
                    </Tag>
                ) : (
                    <SafetyOutlined 
                        style={{ 
                            color: '#faad14', 
                            fontSize: iconSize
                        }} 
                    />
                )}
            </Tooltip>
        );
    }

    if (badges.length === 0) return null;

    return (
        <Space size={4} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {badges}
        </Space>
    );
};

/**
 * CompactBadgeIcons Component
 * Shows only icons without tooltips (for compact spaces)
 */
export const CompactBadgeIcons = ({ user, iconSize = 14 }) => {
    if (!user) return null;

    const icons = [];

    if (user.is_verified) {
        icons.push(
            <CheckCircleOutlined 
                key="verified"
                style={{ color: '#1890ff', fontSize: iconSize }} 
            />
        );
    }

    if (user.seller_trust_badge) {
        icons.push(
            <SafetyCertificateOutlined 
                key="seller-trust"
                style={{ color: '#52c41a', fontSize: iconSize }} 
            />
        );
    }

    if (user.buyer_protection_plus) {
        icons.push(
            <SafetyOutlined 
                key="buyer-protection"
                style={{ color: '#faad14', fontSize: iconSize }} 
            />
        );
    }

    if (icons.length === 0) return null;

    return (
        <Space size={4} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {icons}
        </Space>
    );
};

export default UserBadges;

